import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut, Search, Inbox, ScanSearch, Lock, Trash2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { WhatsAppIcon } from "@/components/WhatsApp";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lapieza_admin_token";

const urgenciaStyle = {
  alta: "bg-brand-orange/15 text-brand-orange border-brand-orange/40",
  media: "bg-brand-amber/15 text-brand-amber border-brand-amber/40",
  baja: "bg-slate-500/15 text-slate-400 border-slate-500/40",
};

const estadoStyle = {
  pendiente: "bg-yellow-500/15 text-yellow-400 border-yellow-500/40",
  falta_datos: "bg-brand-orange/15 text-brand-orange border-brand-orange/40",
  respondida: "bg-green-500/15 text-green-400 border-green-500/40",
};

const estadoLabel = {
  pendiente: "pendiente",
  falta_datos: "falta datos",
  respondida: "respondida",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const normalizePhone = (t) => {
  let p = (t || "").replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  if (p.startsWith("00")) p = p.slice(2);
  if (/^[67]\d{8}$/.test(p)) p = "34" + p;
  return p;
};

const buildWhatsAppMessage = (q, resp) => {
  const lines = [
    `Hola ${q.nombre}, soy de PIEZAS ALJARAFE 24H.`,
    "",
    `Ya tenemos tu presupuesto para: ${q.pieza} (${q.marca} ${q.modelo} ${q.anio}).`,
    "",
    `Precio: ${parseFloat(resp.precio).toFixed(2)} EUR (IVA y envio incluidos)`,
    `Plazo de entrega: ${resp.plazo}`,
  ];
  if (resp.mensaje) lines.push("", resp.mensaje);
  lines.push("", "Responde a este WhatsApp para confirmar tu pedido. Gracias.");
  return lines.join("\n");
};

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success(`Bienvenido, ${data.user.name}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 noise-overlay relative" data-testid="admin-login-view">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm bg-industrial-900 border border-industrial-700 rounded-md p-8 space-y-6"
      >
        <div className="flex flex-col items-center gap-3">
          <Logo className="w-14 h-14" />
          <h1 className="font-display font-black uppercase text-2xl tracking-widest">
            Panel <span className="text-brand-amber">Taller</span>
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Piezas Aljarafe 24H · Acceso restringido</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-email" className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Email</Label>
          <Input id="admin-email" data-testid="admin-username-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-industrial-850 border-industrial-700 h-12 rounded-sm" placeholder="tu@email.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-password" className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Contraseña</Label>
          <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-industrial-850 border-industrial-700 h-12 rounded-sm" placeholder="••••••••" />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit-button"
          className="w-full bg-brand-orange hover:bg-brand-amber disabled:opacity-60 text-white font-display font-black uppercase tracking-widest py-4 rounded-sm transition-colors flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Entrar al panel
        </button>
        <Link to="/" className="flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors" data-testid="admin-back-home-link">
          <ArrowLeft className="w-3 h-3" /> Volver a la web
        </Link>
      </motion.form>
    </div>
  );
}

function QuoteDetail({ quote, onClose, onUpdated, headers }) {
  const [full, setFull] = useState(null);
  const [resp, setResp] = useState({ precio: "", plazo: "24/48h laborables", mensaje: "" });
  const [sending, setSending] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [linkPieza, setLinkPieza] = useState("");
  const [notasInternas, setNotasInternas] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!quote) return;
    setFull(null);
    setConfirmDelete(false);
    axios.get(`${API}/quotes/${quote.id}`, { headers })
      .then(({ data }) => {
        setFull(data);
        setLinkPieza(data.link_pieza || "");
        setNotasInternas(data.notas_internas || "");
        if (data.respuesta) {
          setResp({
            precio: String(data.respuesta.precio),
            plazo: data.respuesta.plazo || "24/48h laborables",
            mensaje: data.respuesta.mensaje || "",
          });
        } else {
          setResp({ precio: "", plazo: "24/48h laborables", mensaje: "" });
        }
      })
      .catch(() => toast.error("No se pudo cargar el detalle"));
  }, [quote, headers]);

  if (!quote) return null;

  const analyzePhoto = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/ai/analyze-photo`, { quote_id: quote.id }, { headers });
      setFull((f) => ({ ...f, ai_analysis: data.analysis }));
      toast.success("Foto analizada por la IA.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo analizar la foto.");
    } finally {
      setAnalyzing(false);
    }
  };

  const sendBudget = async () => {
    if (!resp.precio) {
      toast.error("Indica el precio del presupuesto.");
      return;
    }
    const phone = normalizePhone(full?.telefono || quote.telefono || full?.contacto || quote.contacto);
    if (!phone) {
      toast.error("Esta solicitud no tiene número de WhatsApp.");
      return;
    }
    setSending(true);
    const waWindow = window.open("", "_blank");
    try {
      await axios.patch(`${API}/quotes/${quote.id}/respond`, { ...resp, precio: parseFloat(resp.precio) }, { headers });
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(buildWhatsAppMessage(full || quote, resp))}`;
      if (waWindow) {
        waWindow.location.href = waUrl;
        toast.success("WhatsApp abierto con el presupuesto: pulsa enviar.");
      } else {
        try {
          await navigator.clipboard.writeText(waUrl);
          toast.success("Tu navegador bloqueó la ventana: enlace de WhatsApp copiado, pégalo en el navegador.");
        } catch {
          toast.error("Tu navegador bloqueó la ventana emergente. Permite ventanas emergentes para esta página.");
        }
      }
      onUpdated();
      onClose();
    } catch {
      if (waWindow) waWindow.close();
      toast.error("No se pudo guardar la respuesta.");
    } finally {
      setSending(false);
    }
  };

  const saveLink = async () => {
    setSavingLink(true);
    try {
      await axios.patch(`${API}/quotes/${quote.id}/internal`, { link_pieza: linkPieza, notas_internas: notasInternas }, { headers });
      setFull((f) => ({ ...f, link_pieza: linkPieza, notas_internas: notasInternas }));
      toast.success("Datos internos guardados. Solo tú puedes verlos.");
    } catch {
      toast.error("No se pudieron guardar los datos internos.");
    } finally {
      setSavingLink(false);
    }
  };

  const deleteQuote = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotes/${quote.id}`, { headers });
      toast.success("Solicitud eliminada.");
      onUpdated();
      onClose();
    } catch {
      toast.error("No se pudo eliminar la solicitud.");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const setStatus = async (newStatus) => {
    if (newStatus === quote.status) return;
    try {
      await axios.patch(`${API}/quotes/${quote.id}/status`, { status: newStatus }, { headers });
      toast.success(`Marcada como ${estadoLabel[newStatus]}.`);
      onUpdated();
      onClose();
    } catch {
      toast.error("No se pudo cambiar el estado.");
    }
  };

  const q = full || quote;

  return (
    <Dialog open={!!quote} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-industrial-900 border-industrial-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-wider text-2xl">
            {q.pieza}
          </DialogTitle>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{q.id}</p>
        </DialogHeader>
        {!full ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand-amber" /></div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Cliente", q.nombre],
                ["WhatsApp", q.telefono || q.contacto || "—"],
                ["Vehículo", `${q.marca} ${q.modelo}`],
                ["Año", q.anio],
                ["Matrícula", q.matricula || q.vin || "—"],
                ["Urgencia", q.urgencia],
                ["Recibida", formatDate(q.created_at)],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{k}</p>
                  <p className="text-slate-200 mt-0.5 break-words">{v}</p>
                </div>
              ))}
            </div>
            {q.descripcion && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Detalles del cliente</p>
                <p className="text-sm text-slate-300 bg-industrial-850 border border-industrial-700 rounded-sm p-3">{q.descripcion}</p>
              </div>
            )}
            <div className="border border-dashed border-industrial-600 rounded-sm p-4 space-y-2 bg-industrial-950/50" data-testid="quote-internal-section">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-brand-amber" /> Uso interno · solo visible para ti
              </p>
              <Input
                data-testid="quote-internal-link-input"
                value={linkPieza}
                onChange={(e) => setLinkPieza(e.target.value)}
                placeholder="Link de la pieza: https://... (proveedor, desguace, anuncio)"
                className="bg-industrial-850 border-industrial-700 rounded-sm h-10 text-sm font-mono"
              />
              <Textarea
                data-testid="quote-internal-notes-input"
                value={notasInternas}
                onChange={(e) => setNotasInternas(e.target.value)}
                placeholder="Notas privadas: proveedor, coste, condición de la pieza..."
                className="bg-industrial-850 border-industrial-700 rounded-sm h-20 resize-none text-sm"
              />
              <div className="flex justify-end">
                <button
                  onClick={saveLink}
                  disabled={savingLink}
                  data-testid="quote-internal-link-save-button"
                  className="border border-brand-amber/50 hover:bg-brand-amber/10 text-brand-amber font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60 flex items-center gap-2"
                >
                  {savingLink && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Guardar
                </button>
              </div>
              {full.link_pieza && (
                <a href={full.link_pieza} target="_blank" rel="noopener noreferrer" data-testid="quote-internal-link-open" className="block text-xs text-brand-amber hover:underline truncate font-mono">
                  {full.link_pieza}
                </a>
              )}
            </div>
            {full.foto && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Foto adjunta</p>
                <img src={full.foto} alt="Pieza adjunta por el cliente" className="max-h-56 rounded-sm border border-industrial-700 object-contain" data-testid="quote-detail-photo" />
              </div>
            )}
            {full.has_foto && (
              <div className="space-y-2">
                {full.ai_analysis ? (
                  <div className="bg-brand-orange/10 border border-brand-orange/30 rounded-sm p-4" data-testid="ai-analysis-box">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-brand-amber mb-2 flex items-center gap-1.5">
                      <ScanSearch className="w-3.5 h-3.5" /> Análisis IA de la foto
                    </p>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{full.ai_analysis}</p>
                  </div>
                ) : (
                  <button
                    onClick={analyzePhoto}
                    disabled={analyzing}
                    data-testid="ai-analyze-photo-button"
                    className="w-full border border-dashed border-brand-orange/50 hover:border-brand-amber text-brand-amber font-mono text-xs uppercase tracking-widest py-3 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}
                    {analyzing ? "Analizando foto..." : "Identificar pieza con IA"}
                  </button>
                )}
              </div>
            )}
            {q.respuesta && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4" data-testid="quote-response-summary">
                <p className="font-mono text-[10px] uppercase tracking-widest text-green-400 mb-2">Presupuesto enviado · {formatDate(q.respuesta.responded_at)}</p>
                <p className="font-display font-black text-3xl text-green-400">{q.respuesta.precio.toFixed(2)} €</p>
                <p className="text-sm text-slate-300 mt-1">{q.respuesta.plazo}</p>
                {q.respuesta.mensaje && <p className="text-sm text-slate-400 mt-2">{q.respuesta.mensaje}</p>}
              </div>
            )}
            <div className="space-y-4 border-t border-industrial-700 pt-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-brand-amber">{q.respuesta ? "Editar y reenviar presupuesto" : "Responder con presupuesto"}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Precio (€)</Label>
                    <Input data-testid="quote-response-price-input" type="number" min="0" step="0.01" value={resp.precio} onChange={(e) => setResp((r) => ({ ...r, precio: e.target.value }))} className="bg-industrial-850 border-industrial-700 rounded-sm" placeholder="145.00" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Plazo de entrega</Label>
                    <Input data-testid="quote-response-delivery-input" value={resp.plazo} onChange={(e) => setResp((r) => ({ ...r, plazo: e.target.value }))} className="bg-industrial-850 border-industrial-700 rounded-sm" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Mensaje para el cliente</Label>
                  <Textarea data-testid="quote-response-message-input" value={resp.mensaje} onChange={(e) => setResp((r) => ({ ...r, mensaje: e.target.value }))} className="bg-industrial-850 border-industrial-700 rounded-sm h-20 resize-none" placeholder="Pieza original verificada, envío incluido..." />
                </div>
                <button
                  onClick={sendBudget}
                  disabled={sending}
                  data-testid="quote-response-send-button"
                  className="w-full bg-[#25D366] hover:bg-[#1fb857] disabled:opacity-60 text-white font-display font-black uppercase tracking-widest py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <WhatsAppIcon className="w-4 h-4" />}
                  {q.respuesta ? "Reenviar por WhatsApp" : "Enviar por WhatsApp"}
                </button>
                <p className="text-center font-mono text-[10px] uppercase tracking-widest text-slate-600">
                  Se abre tu WhatsApp con el mensaje listo para el cliente
                </p>
              </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Estado de la solicitud</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "pendiente", l: "Pendiente" },
                  { v: "falta_datos", l: "Falta datos" },
                  { v: "respondida", l: "Respondida" },
                ].map((s) => (
                  <button
                    key={s.v}
                    onClick={() => setStatus(s.v)}
                    data-testid={`quote-status-${s.v}-button`}
                    className={`font-mono text-[10px] uppercase tracking-widest py-3 rounded-sm border transition-colors ${
                      quote.status === s.v
                        ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                        : "border-industrial-600 text-slate-400 hover:text-slate-200 hover:border-slate-500"
                    }`}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            {confirmDelete ? (
              <div className="flex gap-2">
                <button
                  onClick={deleteQuote}
                  disabled={deleting}
                  data-testid="quote-delete-confirm-button"
                  className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-mono text-xs uppercase tracking-widest py-3 rounded-sm transition-colors flex items-center justify-center gap-2"
                >
                  {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Confirmar eliminación
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  data-testid="quote-delete-cancel-button"
                  className="flex-1 border border-industrial-600 text-slate-400 hover:text-slate-200 font-mono text-xs uppercase tracking-widest py-3 rounded-sm transition-colors"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="quote-delete-button"
                className="w-full border border-red-900/60 hover:border-red-500 text-red-500/80 hover:text-red-400 font-mono text-xs uppercase tracking-widest py-3 rounded-sm transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-3.5 h-3.5" /> Eliminar solicitud
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("todas");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const knownIds = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers });
      const prev = knownIds.current;
      if (prev !== null) {
        data.quotes
          .filter((q) => !prev.has(q.id))
          .forEach((q) => toast.info(`Nueva solicitud: ${q.pieza} — ${q.nombre}`, { duration: 10000 }));
      }
      knownIds.current = new Set(data.quotes.map((q) => q.id));
      setQuotes(data.quotes);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else if (!silent) {
        toast.error("No se pudieron cargar las solicitudes.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, headers]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token) return;
    const iv = setInterval(() => load(true), 30000);
    return () => clearInterval(iv);
  }, [token, load]);

  useEffect(() => {
    if (!token) return;
    const pend = quotes.filter((q) => q.status === "pendiente").length;
    document.title = pend > 0 ? `(${pend}) Panel Taller · Piezas Aljarafe 24H` : "Panel Taller · Piezas Aljarafe 24H";
    return () => { document.title = "PIEZAS ALJARAFE 24H — Tu pides la pieza, nosotros la buscamos por ti"; };
  }, [quotes, token]);

  if (!token) return <LoginView onLogin={setToken} />;

  const filtered = quotes.filter((q) => {
    if (filter !== "todas" && q.status !== filter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return [q.nombre, q.marca, q.modelo, q.pieza, q.vin, q.matricula, q.telefono].some((v) => (v || "").toLowerCase().includes(s));
  });

  const kpis = [
    { label: "Solicitudes totales", value: quotes.length, testid: "kpi-total" },
    { label: "Pendientes", value: quotes.filter((q) => q.status === "pendiente").length, testid: "kpi-pending" },
    { label: "Respondidas", value: quotes.filter((q) => q.status === "respondida").length, testid: "kpi-responded" },
    { label: "Urgentes sin responder", value: quotes.filter((q) => q.status === "pendiente" && q.urgencia === "alta").length, testid: "kpi-urgent" },
  ];

  return (
    <div className="min-h-screen bg-industrial-950" data-testid="admin-dashboard">
      <header className="border-b border-industrial-700 bg-industrial-900/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="w-9 h-9" />
            <span className="font-display font-black uppercase tracking-widest text-lg">Panel <span className="text-brand-amber">Taller</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site-link" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors hidden sm:block">
              Ver web
            </Link>
            <button
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-brand-orange transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} className="bg-industrial-900 border border-industrial-700 rounded-sm p-5" data-testid={k.testid}>
              <p className="font-display font-black text-4xl text-brand-amber">{k.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{k.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-industrial-900 border border-industrial-700" data-testid="admin-filter-tabs">
              <TabsTrigger value="todas" data-testid="filter-tab-all">Todas</TabsTrigger>
              <TabsTrigger value="pendiente" data-testid="filter-tab-pending">Pendientes</TabsTrigger>
              <TabsTrigger value="falta_datos" data-testid="filter-tab-missing">Falta datos</TabsTrigger>
              <TabsTrigger value="respondida" data-testid="filter-tab-responded">Respondidas</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, marca, pieza, matrícula..."
              className="bg-industrial-900 border-industrial-700 rounded-sm pl-9 w-full sm:w-80"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-amber" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-slate-500" data-testid="quotes-empty-state">
            <Inbox className="w-10 h-10" />
            <p className="font-mono text-xs uppercase tracking-widest">No hay solicitudes en esta vista</p>
          </div>
        ) : (
          <div className="border border-industrial-700 rounded-sm overflow-hidden" data-testid="quotes-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-industrial-900 border-b border-industrial-700">
                    {["Cliente", "Vehículo", "Pieza", "Urgencia", "Fecha", "Estado"].map((h) => (
                      <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => setSelected(q)}
                      data-testid={`quote-row-${q.id}`}
                      className="border-b border-industrial-700/50 hover:bg-industrial-900/70 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-4 text-slate-200 font-medium">{q.nombre}</td>
                      <td className="px-4 py-4 text-slate-400">{q.marca} {q.modelo} · {q.anio}</td>
                      <td className="px-4 py-4 text-slate-300 max-w-[220px] truncate">{q.pieza}</td>
                      <td className="px-4 py-4">
                        <Badge variant="outline" className={`${urgenciaStyle[q.urgencia]} font-mono text-[10px] uppercase tracking-widest`}>{q.urgencia}</Badge>
                      </td>
                      <td className="px-4 py-4 text-slate-500 font-mono text-xs whitespace-nowrap">{formatDate(q.created_at)}</td>
                      <td className="px-4 py-4">
                        <Badge variant="outline" className={`${estadoStyle[q.status]} font-mono text-[10px] uppercase tracking-widest`} data-testid={`quote-status-${q.id}`}>{estadoLabel[q.status] || q.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <QuoteDetail quote={selected} onClose={() => setSelected(null)} onUpdated={load} headers={headers} />
    </div>
  );
}
